import { Component, OnInit } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import dayjs from 'dayjs';
import { PaginationInstance } from 'ngx-pagination';
import { Observable, Subject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { SiteConfig } from '../../../config/site-config';
import { NotificationType } from '../../shared/models/notification-type';
import { ConfirmDialogComponent } from '../../shared/services/dialog/confirm-dialog/confirm-dialog.component';
import { DialogService } from '../../shared/services/dialog/dialog.service';
import { ErrorHandlerService } from '../../shared/services/error-handler/error-handler.service';
import { NotificationService } from '../../shared/services/notification/notification.service';
import {
    TermsConditionsDialogComponent,
} from '../digital-wallet/components/terms-conditions-dialog/terms-conditions-dialog.component';
import { IRewardShip } from '../models/rewardShip.interface';

import { RewardshipService } from '../services/rewardship.service';
import { RewardshipRulesComponent } from './rewardship-rules/rewardship-rules.component';

@Component({
    selector: 'upsc-rewardship',
    templateUrl: './rewardship.component.html',
    styleUrls: ['./rewardship.component.scss'],
})
export class RewardshipComponent implements OnInit {

    public currentDate = dayjs().format('MM/DD/YYYY');
    public header: any;
    public asyncRewards: Observable<IRewardShip[]>;
    public paginate: PaginationInstance;
    public dtOptions: any = {};
    public dtTrigger: Subject<any> = new Subject<any>();
    public termsAndCondAccepted = true;
    private currentPage = 1;
    private pageSize = 5;
    private totalItems = 0;

    constructor(private rewardshipService: RewardshipService,
                private notificationService: NotificationService,
                private errorHandlerService: ErrorHandlerService,
                private dialogService: DialogService,
                private translateService: TranslateService,
                private dialog: MatDialog,
    ) {
        this.getRewardsHistoryHeader();
    }

    public ngOnInit() {
        this.dtOptions = SiteConfig.tableConfig;

        this.updatePaginateConfig();
        this.getRewardsHistory(this.currentPage, this.pageSize);
        this.getTermsAndCondAccepted();
    }

    getRewardsHistory(pageNumber: number, pageSize: number) {
        this.asyncRewards = this.rewardshipService.getRewardsHistory(pageNumber, pageSize)
                                .pipe(tap(
                                    (rewards) => {

                                        if (rewards && rewards.length > 0) {
                                            this.totalItems = rewards[0].CollectionSize;
                                            this.currentPage = pageNumber;
                                            this.pageSize = pageSize;
                                            this.updatePaginateConfig();

                                            this.dtTrigger.next(null);
                                            return;
                                        }

                                        this.dtTrigger.next(null);
                                        return;
                                    },
                                    error => {
                                        this.notificationService.notify(
                                            this.errorHandlerService.getHttpErrorMessage(error),
                                            this.translateService.instant('FailedGettingRewardsHistory'),
                                            NotificationType.ERROR);
                                        this.dtTrigger.next(null);
                                    }));
    }

    getTermsAndCondAccepted() {
        this.rewardshipService.getTermsAndCondAccepted()
            .subscribe(
                (response) => {
                    this.termsAndCondAccepted = response;
                    if (response) {
                        // Enable page display
                        var elements = document.getElementsByClassName('content-disabled');
                        elements[0].classList.remove('content-disabled');
                    }
                },
                (error) => {
                    this.notificationService.notify(
                        this.errorHandlerService.getHttpErrorMessage(error),
                        this.translateService.instant('errorCheckingTermsAndCondAccepted'),
                        NotificationType.ERROR);
                },
            );
    }

    public acceptTermsConditions() {
        // When Continue button is clicked call Ship API (dw-73 )to set the T&C flag, hide the banner & enable the page.
        this.rewardshipService.postTCFlag().subscribe((response) => {
            // console.log(response);
        });

        // Remove banner display from page
        const bannerElement = document.getElementById('banner');
        bannerElement.remove();

        // Enable page display
        var elements = document.getElementsByClassName('content-disabled');
        elements[0].classList.remove('content-disabled');
    }

    public openTermsConditionsDialog(event: any) {

        event.preventDefault();
        const dialogConfig: MatDialogConfig = {
            disableClose: true,
            width: '60%',
        };

        const dialogRef: MatDialogRef<TermsConditionsDialogComponent> = this.dialog.open(TermsConditionsDialogComponent, dialogConfig);
    }

    public onPageChanged(pageNumber: number) {
        this.getRewardsHistory(pageNumber, this.pageSize);
    }

    getRewardsHistoryHeader() {
        this.rewardshipService.getRewardsHistoryHeader()
            .subscribe(
                (header) => {
                    if (header && header.length > 0) {
                        this.header = header[0];
                    }
                },
                (error) => {
                    this.notificationService.notify(
                        this.errorHandlerService.getHttpErrorMessage(error),
                        this.translateService.instant('errorLoadingRewardShipHeader'),
                        NotificationType.ERROR);
                });
    }

    redeemRewards() {
        this.rewardshipService.redeemRewards().subscribe(
            (res) => {
                this.getRewardsHistoryHeader();
                this.getRewardsHistory(this.currentPage, this.pageSize);
                this.translateService.get('redeemedMsg', { redeemAmount: this.header.AvailableSum })
                    .subscribe(result => {
                        this.notificationService.notify(result, '', NotificationType.SUCCESS);
                    });
            },
            (error) => {
                this.notificationService.notify(
                    this.errorHandlerService.getHttpErrorMessage(error),
                    this.translateService.instant('errorRedeemRewards'),
                    NotificationType.ERROR);
            });
    }

    openRewardShipRules() {
        const dialogConfig: MatDialogConfig = {
            disableClose: true,
            width: '80%',
        };

        let dialogRef: MatDialogRef<RewardshipRulesComponent>;
        dialogRef = this.dialog.open(RewardshipRulesComponent, dialogConfig);

    }

    redeemConfirmation() {
        if (this.header.AvailableSum > 0) {
            const dialogConfig: MatDialogConfig = {
                disableClose: true,
                data: {
                    message:
                        this.translateService.instant('wantToRedeem1') + this.header.AvailableSum + this.translateService.instant('wantToRedeem2'),
                    yes: 'Redeem',
                },
            };

            let confirmDialogRef: MatDialogRef<ConfirmDialogComponent>;
            confirmDialogRef = this.dialog.open(ConfirmDialogComponent, dialogConfig);

            confirmDialogRef.afterClosed().subscribe(
                (result) => {
                    if (result) {
                        this.redeemRewards();
                    }
                });
        } else { //MV3-4001 show no reward points
            this.dialogService.alert(
                '',
                this.translateService.instant('noRedeemPoints_Message'));
        }
    }

    private updatePaginateConfig() {
        this.paginate = {
            totalItems: this.totalItems,
            currentPage: this.currentPage,
            itemsPerPage: this.pageSize,
        };
    }
}
